import LaravelError from '@/api/model/LaravelError';

export type QueryArrayFormat = 'comma' | 'repeat' | 'brackets';
export type ResponseType = 'json' | 'blob' | 'text' | 'arrayBuffer' | 'void';
export type QueryPrimitive = string | number | boolean | Date | null | undefined;
export type QueryValue = QueryPrimitive | QueryPrimitive[];
export type QueryParams = URLSearchParams | Record<string, QueryValue>;

export type HttpErrorContext = {
    status: number;
    url: string;
    response: Response;
    body: unknown;
};

export type FetchHooks = {
    onUnauthorized?: (context: HttpErrorContext) => void | Promise<void>;
    onCsrfMismatch?: (context: HttpErrorContext) => void | Promise<void>;
    getSocketId?: () => string | null | undefined;
};

export type FetchDefaults = FetchHooks & {
    headers?: HeadersInit;
    credentials?: RequestCredentials;
    arrayFormat?: QueryArrayFormat;
};

export type FetchOptions = Omit<RequestInit, 'body'> & {
    body?: BodyInit | object | null;
    query?: QueryParams;
    responseType?: ResponseType;
    arrayFormat?: QueryArrayFormat;
    returnResponse?: false;
};

export type FetchWithResponseOptions = Omit<FetchOptions, 'returnResponse'> & {
    returnResponse: true;
};

export type FetchResponse<T> = {
    data: T;
    status: number;
    headers: Headers;
    response: Response;
};

export type ApiFetch = {
    <T = unknown>(url: string, options?: FetchOptions): Promise<T>;
    <T = unknown>(url: string, options: FetchWithResponseOptions): Promise<FetchResponse<T>>;
};

const JSON_CONTENT_TYPE = 'application/json';
const XSRF_COOKIE = 'XSRF-TOKEN';

export function createFetch(defaults: FetchDefaults = {}): ApiFetch {
    const config: FetchDefaults = {
        credentials: 'include',
        arrayFormat: 'comma',
        ...defaults,
    };

    return (async <T = unknown>(
        url: string,
        options: FetchOptions | FetchWithResponseOptions = {},
    ): Promise<T | FetchResponse<T>> => {
        const method = options.method ?? 'GET';
        const resolvedUrl = buildUrl(url, options.query, options.arrayFormat ?? config.arrayFormat ?? 'comma');
        const headers = buildHeaders(method, options.body, options.headers, config);
        const responseType = options.responseType ?? 'json';

        let response: Response;

        try {
            response = await fetch(resolvedUrl, {
                ...options,
                method,
                credentials: options.credentials ?? config.credentials ?? 'include',
                headers,
                body: prepareBody(options.body, headers),
            });
        } catch (error) {
            throw LaravelError.fromTransportError(error);
        }

        if (response.status === 204 || responseType === 'void') {
            if (!response.ok) {
                await throwResponseError(response, resolvedUrl, config);
            }

            const parsedResponse = {
                data: undefined as T,
                status: response.status,
                headers: response.headers,
                response,
            };

            return options.returnResponse ? parsedResponse : parsedResponse.data;
        }

        if (!response.ok) {
            await throwResponseError(response, resolvedUrl, config);
        }

        const parsedResponse = {
            data: await parseResponse<T>(response, responseType),
            status: response.status,
            headers: response.headers,
            response,
        };

        return options.returnResponse ? parsedResponse : parsedResponse.data;
    }) as ApiFetch;
}

let fetchDefaults: FetchDefaults = {};
export let apiFetch = createFetch(fetchDefaults);

export function configureApiFetch(defaults: FetchDefaults): void {
    fetchDefaults = {
        ...fetchDefaults,
        ...defaults,
        headers: {
            ...Object.fromEntries(headersToObject(fetchDefaults.headers)),
            ...Object.fromEntries(headersToObject(defaults.headers)),
        },
    };
    apiFetch = createFetch(fetchDefaults);
}

function buildUrl(url: string, query: QueryParams | undefined, arrayFormat: QueryArrayFormat): string {
    const resolvedUrl = new URL(url, globalThis.window?.location?.origin ?? 'http://localhost');
    const params = normalizeQueryParams(query, arrayFormat);

    params.forEach((value, key) => {
        resolvedUrl.searchParams.append(key, value);
    });

    return resolvedUrl.toString();
}

function normalizeQueryParams(query: QueryParams | undefined, arrayFormat: QueryArrayFormat): URLSearchParams {
    if (!query) {
        return new URLSearchParams();
    }

    if (query instanceof URLSearchParams) {
        return query;
    }

    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
        appendQueryValue(params, key, value, arrayFormat);
    });

    return params;
}

function appendQueryValue(
    params: URLSearchParams,
    key: string,
    value: QueryValue,
    arrayFormat: QueryArrayFormat,
): void {
    if (value === null || value === undefined) {
        return;
    }

    if (Array.isArray(value)) {
        const values = value.filter((item): item is Exclude<QueryPrimitive, null | undefined> => {
            return item !== null && item !== undefined;
        });

        if (arrayFormat === 'comma') {
            if (values.length > 0) {
                params.append(key, values.map(stringifyQueryValue).join(','));
            }
            return;
        }

        values.forEach((item) => {
            params.append(arrayFormat === 'brackets' ? `${key}[]` : key, stringifyQueryValue(item));
        });
        return;
    }

    params.append(key, stringifyQueryValue(value));
}

function stringifyQueryValue(value: Exclude<QueryPrimitive, null | undefined>): string {
    if (value instanceof Date) {
        return value.toISOString();
    }

    return String(value);
}

function buildHeaders(
    method: string,
    body: FetchOptions['body'],
    optionHeaders: HeadersInit | undefined,
    config: FetchDefaults,
): Headers {
    const headers = new Headers(config.headers);

    headers.set('Accept', headers.get('Accept') ?? JSON_CONTENT_TYPE);
    headers.set('X-Requested-With', headers.get('X-Requested-With') ?? 'Sellopod Web App');

    headersToObject(optionHeaders).forEach(([key, value]) => {
        headers.set(key, value);
    });

    if (isJsonBody(body) && !headers.has('Content-Type')) {
        headers.set('Content-Type', JSON_CONTENT_TYPE);
    }

    const xsrfToken = readCookie(XSRF_COOKIE);
    if (xsrfToken && !headers.has('X-XSRF-TOKEN')) {
        headers.set('X-XSRF-TOKEN', decodeURIComponent(xsrfToken));
    }

    if (isMutatingMethod(method)) {
        const socketId = config.getSocketId?.();
        if (socketId && !headers.has('X-Socket-ID')) {
            headers.set('X-Socket-ID', socketId);
        }
    }

    return headers;
}

function headersToObject(headers: HeadersInit | undefined): Array<[string, string]> {
    if (!headers) {
        return [];
    }

    if (headers instanceof Headers) {
        return Array.from(headers.entries());
    }

    if (Array.isArray(headers)) {
        return headers;
    }

    return Object.entries(headers);
}

function prepareBody(body: FetchOptions['body'], headers: Headers): BodyInit | null | undefined {
    if (body === null || body === undefined) {
        return body;
    }

    if (isJsonBody(body)) {
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', JSON_CONTENT_TYPE);
        }

        return JSON.stringify(body);
    }

    return body as BodyInit;
}

function isJsonBody(body: FetchOptions['body']): body is object {
    return body !== null && body !== undefined && typeof body === 'object' && !isBodyInit(body);
}

function isBodyInit(body: object): boolean {
    return (
        isInstanceOf(body, 'FormData') ||
        isInstanceOf(body, 'Blob') ||
        isInstanceOf(body, 'ArrayBuffer') ||
        isInstanceOf(body, 'URLSearchParams') ||
        isInstanceOf(body, 'ReadableStream')
    );
}

function isInstanceOf(body: object, constructorName: string): boolean {
    const constructor = globalThis[constructorName as keyof typeof globalThis] as
        | (new (...args: never[]) => object)
        | undefined;

    return typeof constructor === 'function' && body instanceof constructor;
}

async function throwResponseError(response: Response, url: string, config: FetchDefaults): Promise<never> {
    const body = await parseErrorBody(response);
    const context = { status: response.status, url, response, body };

    if (response.status === 401) {
        await config.onUnauthorized?.(context);
    }

    if (response.status === 419) {
        await config.onCsrfMismatch?.(context);
    }

    throw LaravelError.fromHttpResponse(context);
}

async function parseErrorBody(response: Response): Promise<unknown> {
    const contentType = response.headers.get('Content-Type') ?? '';

    if (contentType.includes(JSON_CONTENT_TYPE)) {
        return response.json().catch(() => null);
    }

    return response.text().catch(() => null);
}

async function parseResponse<T>(response: Response, responseType: ResponseType): Promise<T> {
    if (responseType === 'blob') {
        return response.blob() as Promise<T>;
    }

    if (responseType === 'text') {
        return response.text() as Promise<T>;
    }

    if (responseType === 'arrayBuffer') {
        return response.arrayBuffer() as Promise<T>;
    }

    return response.json() as Promise<T>;
}

function isMutatingMethod(method: string): boolean {
    return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

function readCookie(name: string): string | null {
    const cookies = globalThis.document?.cookie?.split(';') ?? [];
    const cookie = cookies.map((item) => item.trim()).find((item) => item.startsWith(`${name}=`));

    return cookie ? cookie.substring(name.length + 1) : null;
}
